import { Link } from "react-router-dom";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqs } from "@/mocks/faqs";
import { siteMeta } from "@/mocks/siteMeta";

const quickLinks = [
  { label: "专区总览", to: "/zones", icon: "ri-layout-grid-fill", desc: "全部栏目与板块入口" },
  { label: "内容热榜", to: "/ranking", icon: "ri-fire-line", desc: "多维度的排行与解读" },
  { label: "关于我们", to: "/about", icon: "ri-information-line", desc: "品牌定位与内容原则" },
];

export default function FaqPage() {
  useSeo({
    title: "常见问题 - 爆料91专区使用说明与答疑",
    description:
      "汇总爆料91专区的常见问题：专区与普通分类页的区别、合集多久更新一次、为什么部分内容只有摘要、如何反馈内容问题，帮你快速了解使用方式。",
    keywords: "爆料91常见问题,爆料91专区说明,爆料91合集更新,内容反馈",
    path: "/faq",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE_ORIGIN}/faq` },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-700">
              <span className="w-5 h-px bg-primary-500"></span>
              FAQ
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              常见问题
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              关于专区怎么用、合集更新到什么程度、内容从哪里来，我们把最常被问到的问题集中收在这里。
              如果这里没有你想找的答案，欢迎直接给编辑组发邮件。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-8">
              <FaqAccordion items={faqs} defaultOpenId={faqs[0]?.id} />
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading font-bold text-lg text-foreground-950">还没解决？</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  内容纠错请附上专区名称与具体位置；线索建议简单说明方向即可，
                  我们会安排对应栏目编辑跟进。
                </p>
                <a
                  href={`mailto:${siteMeta.email}`}
                  className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
                >
                  <i className="ri-mail-send-line"></i>
                  发送邮件咨询
                </a>
                <p className="mt-3 text-xs text-foreground-500 break-all">{siteMeta.email}</p>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading font-bold text-lg text-foreground-950">常用入口</h2>
                <div className="mt-4 flex flex-col gap-3">
                  {quickLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="group flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 shrink-0">
                        <i className={link.icon}></i>
                      </span>
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                          {link.label}
                        </span>
                        <span className="mt-0.5 text-xs text-foreground-500">{link.desc}</span>
                      </span>
                      <i className="ri-arrow-right-line ml-auto text-foreground-400 group-hover:text-primary-600 transition-colors"></i>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}